import { z } from 'zod';

// ----- request log -----

export const requestLogStatusSchema = z.enum(['success', 'failure']);
export type RequestLogStatus = z.infer<typeof requestLogStatusSchema>;

export const requestLogLastHoursSchema = z.union([
  z.literal(1),
  z.literal(12),
  z.literal(24),
]);
export type RequestLogLastHours = z.infer<typeof requestLogLastHoursSchema>;

export const requestLogOrderBySchema = z.enum([
  'startTime',
  'duration',
  'responseHttpCode',
]);
export type RequestLogOrderBy = z.infer<typeof requestLogOrderBySchema>;

export const requestLogAuthStateSchema = z.enum(['anon', 'user', 'service']);
export type RequestLogAuthState = z.infer<typeof requestLogAuthStateSchema>;

export const requestLogsListInput = z.object({
  offset: z.number().int().min(0).default(0),
  limit: z.number().int().min(1).max(100).default(10),
  lastHours: requestLogLastHoursSchema.optional(),
  status: requestLogStatusSchema.optional(),
  search: z.string().trim().max(200).optional(),
  // Prefix of the caller reference. The column stores 8 chars, but an operator
  // may type fewer, so this matches left-anchored rather than exactly.
  userRef: z.string().trim().max(16).optional(),
  orderBy: requestLogOrderBySchema.default('startTime'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type RequestLogsListInput = z.infer<typeof requestLogsListInput>;

export const requestLogItemSchema = z.object({
  id: z.number().int(),
  url: z.string(),
  httpMethod: z.string(),
  module: z.string().nullable(),
  context: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  duration: z.string(),
  requestSize: z.string().nullable(),
  responseSize: z.string().nullable(),
  ip: z.string().nullable(),
  responseHttpCode: z.number().int().nullable(),
  error: z.unknown().nullable(),
  // Attribution. All nullable: rows written before this shipped carry NULL,
  // and the UI must render them rather than break.
  userRef: z.string().nullable(),
  authState: requestLogAuthStateSchema.nullable(),
  deniedPermission: z.string().nullable(),
  requestId: z.string().nullable(),
});
export type RequestLogItem = z.infer<typeof requestLogItemSchema>;

export const requestLogsListResultSchema = z.object({
  data: z.array(requestLogItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type RequestLogsListResult = z.infer<typeof requestLogsListResultSchema>;

// ----- benchmark -----

export const benchmarkMethodSchema = z.enum([
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
]);
export type BenchmarkMethod = z.infer<typeof benchmarkMethodSchema>;

export const benchmarkRunInput = z.object({
  url: z.url(),
  method: benchmarkMethodSchema,
  headers: z.record(z.string(), z.string()).optional(),
  query: z.record(z.string(), z.string()).optional(),
  body: z.string().max(65_536).optional(),
  connections: z.number().int().min(1).max(50).default(1),
  pipelining: z.number().int().min(1).max(10).default(1),
  durationSec: z.number().int().min(1).max(10).default(1),
});
export type BenchmarkRunInput = z.infer<typeof benchmarkRunInput>;

export const benchmarkResultSchema = z.object({
  requests: z.object({
    total: z.number(),
    perSecond: z.number(),
  }),
  throughput: z.object({
    bytesTotal: z.number(),
    bytesPerSecond: z.number(),
  }),
  latency: z.object({
    avgMs: z.number(),
    minMs: z.number(),
    maxMs: z.number(),
  }),
  statusCodes: z.object({
    success: z.number().int().nonnegative(),
    failure: z.number().int().nonnegative(),
  }),
  errorDetails: z
    .array(
      z.object({
        code: z.number().int(),
        count: z.number().int().nonnegative(),
      })
    )
    .optional(),
  responseStructure: z.string(),
});
export type BenchmarkResult = z.infer<typeof benchmarkResultSchema>;

export const benchmarkSnapshotInput = z.object({
  name: z.string().min(1).max(120),
  url: z.url(),
  method: benchmarkMethodSchema,
  queryParams: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  responseStructure: z.string().max(65_536),
  benchmarkResult: benchmarkResultSchema,
});
export type BenchmarkSnapshotInput = z.infer<typeof benchmarkSnapshotInput>;

export const benchmarkSnapshotItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
  method: z.string(),
  queryParams: z.string().nullable(),
  body: z.string().nullable(),
  responseStructure: z.string(),
  benchmarkResult: benchmarkResultSchema,
  createdOn: z.string(),
});
export type BenchmarkSnapshotItem = z.infer<typeof benchmarkSnapshotItemSchema>;

export const findBenchmarkSnapshotInput = z.object({
  url: z.url(),
});
export type FindBenchmarkSnapshotInput = z.infer<
  typeof findBenchmarkSnapshotInput
>;

// ----- inconsistency resolver -----

/**
 * The catalog row shown per check in the Inconsistency Resolver. `interactive`
 * gates the run/edit UI — Milestone 1 only wires `product_start_after_end`.
 */
export const inconsistencyCheckCatalogItemSchema = z.object({
  key: z.string(),
  description: z.string(),
  interactive: z.boolean(),
  /**
   * The exact SQL the Resolver runs for this check, for the "View SQL" modal.
   * `null` for checks that are not yet runnable (no query to show).
   */
  sqlText: z.string().nullable(),
});
export type InconsistencyCheckCatalogItem = z.infer<
  typeof inconsistencyCheckCatalogItemSchema
>;

/**
 * One entry in the top-of-page overview: a check's flagged-row count, org-scoped.
 * `count` is `null` when the check can't be counted cheaply — `product_tz_location_mismatch`
 * is JS-evaluated (no SQL predicate), so its number is unknown until the check is
 * run. `item_legs_time_mismatch`'s count comes from the persisted nightly report
 * (a whole-org live count is the scan the Resolver deliberately avoids), so it is
 * an approximate hint rather than the exact refined count.
 */
export const inconsistencyCheckCountSchema = z.object({
  key: z.string(),
  count: z.number().int().nonnegative().nullable(),
});
export type InconsistencyCheckCount = z.infer<
  typeof inconsistencyCheckCountSchema
>;

export const runInconsistencyCheckInput = z.object({
  key: z.string(),
  /**
   * Scopes the run to a single itinerary. Required by `item_legs_time_mismatch`:
   * a whole-org scan of that check walks ~¾M items doing per-row jsonb + regex +
   * timezone math, which stalls the request — so the UI first shows a dropdown of
   * the itineraries that have a finding (from the persisted nightly report, see
   * `mismatchItinerariesResultSchema`) and passes the picked id here. Ignored by
   * the other checks (they are already org-bounded and fast).
   */
  itineraryId: z.string().nullable().optional(),
});
export type RunInconsistencyCheckInput = z.infer<
  typeof runInconsistencyCheckInput
>;

/**
 * One itinerary offered in the `item_legs_time_mismatch` picker: it has at least
 * one finding in the persisted nightly report (`system.inconsistency_report`,
 * `procedure_key = 'item_legs_time_mismatch'`), org-scoped via the itinerary's
 * `organization_id`. `count` is the persisted (raw, pre-timezone-refinement)
 * finding count — a hint, not a promise: the live refined run for the itinerary
 * may return slightly fewer once the timezone-aware comparison clears the
 * false positives. Sorted by `count` desc so the worst offenders surface first.
 */
export const mismatchItinerarySchema = z.object({
  itineraryId: z.string(),
  title: z.string().nullable(),
  count: z.number().int().nonnegative(),
});
export type MismatchItinerary = z.infer<typeof mismatchItinerarySchema>;

export const mismatchItinerariesResultSchema = z.array(mismatchItinerarySchema);
export type MismatchItinerariesResult = z.infer<
  typeof mismatchItinerariesResultSchema
>;

const legTimeSchema = z.object({
  hours: z.number().int().min(0).max(23),
  minutes: z.number().int().min(0).max(59),
});
export type LegTime = z.infer<typeof legTimeSchema>;

/**
 * One flagged leg of a product surfaced by `product_start_after_end`. A product
 * with multiple offending legs produces multiple rows (distinct `legIndex`).
 */
export const inconsistencyProductRowSchema = z.object({
  productId: z.string(),
  titleEn: z.string().nullable(),
  category: z.string().nullable(),
  pricingType: z.string().nullable(),
  legIndex: z.number().int().nonnegative(),
  startTime: legTimeSchema,
  endTime: legTimeSchema,
});
export type InconsistencyProductRow = z.infer<
  typeof inconsistencyProductRowSchema
>;

/**
 * One flagged itinerary item surfaced by the item checks
 * (`item_start_after_end`, `item_null_times`). The signal lives on the item's
 * real `startTime` / `endTime` *timestamp* columns (date + time) — an overnight
 * item is stored with an end date after its start date, so it is never a false
 * positive and no Accommodation/Night/00:00 carve-out is needed. The row ships
 * the item's timestamps alongside the item-leg and product-leg wall-clock times
 * so a reviewer can see where the drift is (check #2) or what the missing time
 * should be (check #3). `startTime`/`endTime` are ISO timestamp strings (null
 * for a missing timestamp); the `*LegTime` fields are the first leg's
 * `{hours,minutes}` (null when there is no leg).
 *
 * `itineraryVersionId` and `versionStartTime` support inline editing: the
 * editor edits a calendar date + HH:MM per row, and the client converts the
 * date to the `dayIndex` that `itineraryItems.update` expects, measured from
 * the version's start date (`versionStartTime`, null for an undated version —
 * excluded from #3 anyway).
 */
export const inconsistencyItemRowSchema = z.object({
  itineraryItemId: z.string(),
  itineraryId: z.string().nullable(),
  itineraryVersionId: z.string().nullable(),
  versionStartTime: z.string().nullable(),
  productId: z.string().nullable(),
  titleEn: z.string().nullable(),
  category: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  itemLegStartTime: legTimeSchema.nullable(),
  itemLegEndTime: legTimeSchema.nullable(),
  productLegStartTime: legTimeSchema.nullable(),
  productLegEndTime: legTimeSchema.nullable(),
});
export type InconsistencyItemRow = z.infer<typeof inconsistencyItemRowSchema>;

/**
 * One flagged leg surfaced by `product_timezone_mismatch`. The refined check
 * only returns legs whose start and end are the SAME location (same city +
 * country, treating a blank end location as "same") yet carry different IANA
 * timezones — an unambiguous data error where the location never actually
 * moved. The correct fix is deterministic: set the end timezone equal to the
 * start timezone (`startTimezone` is the expected value), which is why this
 * check is fixed with a single bulk action rather than a per-row editor.
 * Genuine cross-zone transport (Porto → Santiago, Tarifa → Tangier) has a
 * different end location and is excluded upstream, so it never appears here.
 * The city/country fields are shipped as read-only context so a reviewer can
 * confirm the location is the same on both ends before applying the fix.
 */
export const inconsistencyProductTimezoneRowSchema = z.object({
  productId: z.string(),
  titleEn: z.string().nullable(),
  category: z.string().nullable(),
  legIndex: z.number().int().nonnegative(),
  startTimezone: z.string(),
  endTimezone: z.string(),
  startCity: z.string().nullable(),
  startCountry: z.string().nullable(),
  endCity: z.string().nullable(),
  endCountry: z.string().nullable(),
});
export type InconsistencyProductTimezoneRow = z.infer<
  typeof inconsistencyProductTimezoneRowSchema
>;

/**
 * One flagged itinerary item surfaced by `item_empty_legs`: a non-deleted item
 * whose `legs` jsonb is null / `[]` / zero-length. Unlike the time checks there
 * is nothing on the item itself to recover from — the fix copies the joined
 * product's legs onto the item (`itineraryItems.update` with `patch.legs`). So
 * the row ships **both** full legs arrays: `itemLegs` (empty, shown as a ✗ that
 * opens an empty structured view) and `productLegs` (the source of the copy,
 * shown as a ✓/✗ that opens the structured legs). A row whose product also has
 * no legs (`productLegs` empty/null) has nothing to copy and is not fixable —
 * the UI disables its Copy action and checkbox. `productId`/`itineraryId` back
 * the deep links; the legs are opaque jsonb (arbitrary leg shape), so they are
 * typed as `unknown` arrays and rendered defensively.
 */
export const inconsistencyItemEmptyLegsRowSchema = z.object({
  itineraryItemId: z.string(),
  itineraryId: z.string().nullable(),
  productId: z.string().nullable(),
  titleEn: z.string().nullable(),
  category: z.string().nullable(),
  itemLegs: z.array(z.unknown()).nullable(),
  productLegs: z.array(z.unknown()).nullable(),
});
export type InconsistencyItemEmptyLegsRow = z.infer<
  typeof inconsistencyItemEmptyLegsRowSchema
>;

/**
 * One flagged itinerary item surfaced by `item_legs_time_mismatch`: the item's
 * own `start_time`/`end_time` (UTC timestamps) disagree with the wall-clock
 * times its legs declare — the first leg's `startTime` vs the item start, and
 * the last leg's `endTime` vs the item end. The refined check compares
 * apples-to-apples: it converts the item's UTC instant into the leg's own IANA
 * timezone before comparing hours/minutes (a naive UTC-vs-local comparison
 * false-flags every non-UTC item), and skips legs whose time keys are
 * missing/non-numeric or whose timezone is not a valid IANA name.
 *
 * The row ships everything the two-direction fix needs:
 *  - `startTime`/`endTime` — the item's UTC timestamps (ISO strings, nullable).
 *  - `startTimezone`/`endTimezone` — the first-leg start / last-leg end IANA
 *    zones (null → treat as UTC).
 *  - `legStartTime`/`legEndTime` — the first-leg start / last-leg end wall-clock
 *    `{hours,minutes}` (null when the leg carries no time).
 *  - `itineraryVersionId`/`versionStartTime` — anchor for the `dayIndex` the
 *    "use legs" direction feeds `itineraryItems.update`.
 *  - `legs` — the item's FULL legs jsonb, so the "use item" direction can
 *    rewrite `legs[0].startTime` / `legs[last].endTime` and round-trip the rest
 *    untouched.
 *
 * Two deterministic fixes, chosen by the reviewer per row (or in bulk):
 *  - "Use legs"  → item start/end := the leg wall-clock times (converted from
 *    the leg zone back to UTC HH:MM), keeping the item's calendar day.
 *  - "Use item"  → leg times := the item's UTC times (converted into each leg's
 *    zone), rewritten into the legs jsonb.
 */
export const inconsistencyItemLegsTimeMismatchRowSchema = z.object({
  itineraryItemId: z.string(),
  itineraryId: z.string().nullable(),
  itineraryVersionId: z.string().nullable(),
  versionStartTime: z.string().nullable(),
  productId: z.string().nullable(),
  titleEn: z.string().nullable(),
  category: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  startTimezone: z.string().nullable(),
  endTimezone: z.string().nullable(),
  legStartTime: legTimeSchema.nullable(),
  legEndTime: legTimeSchema.nullable(),
  legs: z.array(z.unknown()).nullable(),
});
export type InconsistencyItemLegsTimeMismatchRow = z.infer<
  typeof inconsistencyItemLegsTimeMismatchRowSchema
>;

/**
 * One flagged itinerary surfaced by `sold_itinerary_no_sold_on`: it sits at
 * sales_stage 10 (SOLD) yet has no `sold_on` timestamp. This check does NOT
 * over-flag — stage 10 is the canonical SOLD stage, so every row is a genuinely
 * sold itinerary missing its sale date. The row carries display context
 * (`title`, `itineraryRef`, `optigest` = the active version's accounting
 * reference, `statusName` + `salesStage`) plus `proposedSoldOn`: the sale date
 * recovered from the operation log (the earliest `itinerary_status_change` into
 * a stage-10 status), or null when the log has no such transition. The fix
 * writes the chosen date via `itineraries.update` `patch: { soldOn }` — the
 * reviewer either accepts the proposal or sets the date manually.
 */
export const inconsistencySoldNoSoldOnRowSchema = z.object({
  itineraryId: z.string(),
  title: z.string().nullable(),
  itineraryRef: z.string().nullable(),
  optigest: z.string().nullable(),
  statusName: z.string().nullable(),
  salesStage: z.number().int().nullable(),
  proposedSoldOn: z.string().nullable(),
});
export type InconsistencySoldNoSoldOnRow = z.infer<
  typeof inconsistencySoldNoSoldOnRowSchema
>;

/**
 * One flagged product leg surfaced by `product_tz_location_mismatch`: the leg's
 * START location carries a stored IANA timezone that a location-derived
 * suggester (`createTimezoneSuggester` — coords → `tz-lookup`, else the
 * country's authoritative zone, else a geocode) either DISAGREES with
 * (`category: 'mismatch'`, a fixable data error), or CANNOT decide
 * (`category: 'ambiguous'` — multi-timezone country with no usable coordinates
 * / address / single-zone country; `suggestedTimezone` is null and a human must
 * pick). Unlike the SQL `product_timezone_mismatch` check (start-tz ≠ end-tz),
 * this compares the stored zone against the *location*, so the raw material is
 * the leg's coordinates + address + city + country, all shipped for display.
 *
 * The row carries everything the bulk fix needs:
 *  - `productId`/`legIndex` — a product may contribute more than one flagged leg.
 *  - `currentTimezone` — the stored start-location zone (the value being judged).
 *  - `suggestedTimezone` — the resolver's answer, or null when ambiguous.
 *  - `suggestionSource` — how it was derived (`location-point` / `country-single-tz`
 *    / `geocoded`), or null when ambiguous — shown as a provenance badge.
 *  - `latitude`/`longitude`/`address`/`city`/`country` — location context and a
 *    Maps deep link, so the reviewer can confirm before applying.
 * The fix writes the chosen zone onto BOTH `startLocation.timezone` and
 * `endLocation.timezone` of the leg (a single-location leg — a genuine cross-zone
 * journey is out of scope here) via `products.update` `patch: { legs }`.
 */
export const inconsistencyTzLocationRowSchema = z.object({
  productId: z.string(),
  titleEn: z.string().nullable(),
  category: z.enum(['mismatch', 'ambiguous']),
  legIndex: z.number().int().nonnegative(),
  currentTimezone: z.string().nullable(),
  suggestedTimezone: z.string().nullable(),
  suggestionSource: z
    .enum(['country-single-tz', 'location-point', 'geocoded'])
    .nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
});
export type InconsistencyTzLocationRow = z.infer<
  typeof inconsistencyTzLocationRowSchema
>;

/**
 * Discriminated by `kind` so the Resolver panel can render the right table:
 * `product` checks (editable leg times), `item` checks (editable date+time),
 * `productTimezone` (one-click bulk fix — end timezone := start timezone),
 * `itemEmptyLegs` (selectable bulk copy — item legs := product legs), and
 * `itemLegsTimeMismatch` (two-direction fix — use legs OR use item), and
 * `soldNoSoldOn` (per-row date fix — accept the log-proposed sold date or set
 * one manually), and `tzLocation` (per-row timezone select seeded with a
 * location-derived suggestion — bulk apply). Exactly one of the `*Rows` arrays
 * is populated per result.
 */
export const runInconsistencyCheckResultSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('product'),
    key: z.string(),
    productRows: z.array(inconsistencyProductRowSchema),
  }),
  z.object({
    kind: z.literal('item'),
    key: z.string(),
    itemRows: z.array(inconsistencyItemRowSchema),
  }),
  z.object({
    kind: z.literal('productTimezone'),
    key: z.string(),
    timezoneRows: z.array(inconsistencyProductTimezoneRowSchema),
  }),
  z.object({
    kind: z.literal('itemEmptyLegs'),
    key: z.string(),
    itemEmptyLegsRows: z.array(inconsistencyItemEmptyLegsRowSchema),
  }),
  z.object({
    kind: z.literal('itemLegsTimeMismatch'),
    key: z.string(),
    itemLegsTimeMismatchRows: z.array(
      inconsistencyItemLegsTimeMismatchRowSchema
    ),
  }),
  z.object({
    kind: z.literal('soldNoSoldOn'),
    key: z.string(),
    soldNoSoldOnRows: z.array(inconsistencySoldNoSoldOnRowSchema),
  }),
  z.object({
    kind: z.literal('tzLocation'),
    key: z.string(),
    tzLocationRows: z.array(inconsistencyTzLocationRowSchema),
  }),
]);
export type RunInconsistencyCheckResult = z.infer<
  typeof runInconsistencyCheckResultSchema
>;
